import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowRight, Loader2, RefreshCw, Search, Store } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import ConfirmAlertDialog from '@/components/ui/confirm-alert-dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import EtsyWorkspaceNav from '@/components/etsy/EtsyWorkspaceNav';
import { WORKSPACE_CANVAS_MODES } from '@/components/workspace/workspaceMode';
import { ETSY_SELECTED_ORDER_QUERY_KEY } from '@/components/etsy/etsyWorkspaceFlow';
import { useAppSelector } from '@/app/hooks';
import { selectCurrentService } from '@/features/service/serviceSlice';
import { fetchPendingEtsyOrders, type EtsyPendingOrder } from '@/services/etsyService';
import { logging } from '@/services/logging';
import { openEtsyOrderInProductionFlow } from '@/components/etsy/openEtsyOrderInProductionFlow';
import { EtsyOrderPreviewThumbnail } from '@/components/etsy/EtsyOrdersCanvas';
import {
  getEtsyProductionWarningSummary,
  getEtsyProductionWarnings,
} from '@/components/etsy/etsyProductionWarnings';

type MissingArea = 'papers' | 'sizes' | 'addresses' | 'images';

const getMissingAreas = (order: EtsyPendingOrder): MissingArea[] => {
  const missing: MissingArea[] = [];

  if (!order.items.every((item) => item.matchedPaperType && item.paperReviewStatus === 'CONFIRMED')) {
    missing.push('papers');
  }

  if (!order.items.every((item) => item.matchedWidthIn != null && item.matchedHeightIn != null)) {
    missing.push('sizes');
  }

  if (!(order.matchedAddress && order.addressReviewStatus === 'CONFIRMED')) {
    missing.push('addresses');
  }

  if (!order.items.every((item) => item.fullSizeUploadedFile != null)) {
    missing.push('images');
  }

  return missing;
};

const isReadyForProduction = (order: EtsyPendingOrder) => getMissingAreas(order).length === 0;

const canOpenInProduction = (order: EtsyPendingOrder) =>
  order.queueStatus === 'PENDING' || order.queueStatus === 'IN_PRODUCTION';

const formatDateTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const buildSearchIndex = (order: EtsyPendingOrder) =>
  [
    order.etsyReceiptId,
    order.shopName,
    order.etsyData.name,
    order.etsyData.buyer_email,
    ...order.items.map((item) => item.title),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

const getMissingAreaLabel = (area: MissingArea, translate: (key: string, fallback: string) => string) => (
  area === 'papers'
    ? translate('integrations.etsy.toProduction.summary.papers', 'Papers')
    : area === 'sizes'
      ? translate('integrations.etsy.toProduction.summary.sizes', 'Sizes')
      : area === 'addresses'
        ? translate('integrations.etsy.toProduction.summary.addresses', 'Address')
        : translate('integrations.etsy.toProduction.summary.images', 'Full-size image')
);

const EtsyToProductionCanvas = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentService = useAppSelector(selectCurrentService);
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState<ReadonlyArray<EtsyPendingOrder>>([]);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [confirmOrderId, setConfirmOrderId] = useState<string | null>(null);
  const [openingOrderId, setOpeningOrderId] = useState<string | null>(null);

  const loadOrders = useCallback(async () => {
    try {
      setStatus('loading');
      setError(null);
      const result = await fetchPendingEtsyOrders({ includeCompleted: true });
      setOrders(result);
      setStatus('idle');
    } catch (loadError) {
      logging.error('Failed to load Etsy to-production orders', { error: loadError });
      setError(loadError instanceof Error ? loadError.message : 'Failed to load Etsy orders.');
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    void loadOrders();
  }, [loadOrders]);

  const filteredOrders = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const inboxOrders = orders.filter((order) => order.selectedForInbox);

    if (!normalizedQuery) {
      return inboxOrders;
    }

    return inboxOrders.filter((order) => buildSearchIndex(order).includes(normalizedQuery));
  }, [orders, query]);

  const sortedOrders = useMemo(() => filteredOrders.slice().sort((left, right) => {
    const leftPriority = isReadyForProduction(left) ? 1 : 0;
    const rightPriority = isReadyForProduction(right) ? 1 : 0;
    if (leftPriority !== rightPriority) {
      return leftPriority - rightPriority;
    }

    return right.etsyReceiptId.localeCompare(left.etsyReceiptId, undefined, { numeric: true });
  }), [filteredOrders]);

  const selectedOrderId = searchParams.get(ETSY_SELECTED_ORDER_QUERY_KEY);
  const selectedOrder = sortedOrders.find((order) => order.id === selectedOrderId) ?? sortedOrders[0] ?? null;
  const selectedMissingAreas = selectedOrder ? getMissingAreas(selectedOrder) : [];
  const selectedProductionWarnings = selectedOrder ? getEtsyProductionWarnings(selectedOrder) : [];
  const selectedProductionWarningSummary = getEtsyProductionWarningSummary(selectedProductionWarnings, t);

  useEffect(() => {
    if (!selectedOrder || selectedOrder.id === selectedOrderId) {
      return;
    }

    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set(ETSY_SELECTED_ORDER_QUERY_KEY, selectedOrder.id);
      return next;
    });
  }, [selectedOrder, selectedOrderId, setSearchParams]);

  const handleOpen = async (order: EtsyPendingOrder) => {
    try {
      setOpeningOrderId(order.id);
      await openEtsyOrderInProductionFlow({
        importedOrderId: order.id,
        etsyReceiptId: order.etsyReceiptId,
        currentServiceId: currentService?.id ?? null,
        navigate,
      });
    } finally {
      setOpeningOrderId(null);
      setConfirmOrderId(null);
    }
  };

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <Card className="border-border/70">
        <CardHeader className="gap-4">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
            <div className="space-y-2">
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-muted/20 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <Store className="h-3.5 w-3.5" />
                {t('integrations.etsy.toProduction.badge', 'Etsy handoff')}
              </div>
              <div>
                <CardTitle className="text-xl">
                  {t('integrations.etsy.toProduction.title', 'Etsy To Production')}
                </CardTitle>
                <CardDescription className="mt-1 max-w-3xl">
                  {t('integrations.etsy.toProduction.description', 'Review what is still missing for each inbox order before opening it in the production flow. Matching is recommended, but not required.')}
                </CardDescription>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative min-w-[280px]">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={t('integrations.etsy.toProduction.searchPlaceholder', 'Search Etsy inbox orders')}
                  className="pl-9"
                />
              </div>
              <Button variant="outline" size="sm" onClick={() => void loadOrders()} disabled={status === 'loading'}>
                {status === 'loading' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                {t('common.reload', 'Reload')}
              </Button>
            </div>
          </div>
          <EtsyWorkspaceNav activeMode={WORKSPACE_CANVAS_MODES.etsyToProduction} />
        </CardHeader>
      </Card>

      <div className="grid min-h-0 flex-1 gap-4 xl:grid-cols-[minmax(0,1.25fr)_minmax(360px,0.9fr)]">
        <Card className="min-h-0">
          <CardHeader>
            <CardTitle>{t('integrations.etsy.toProduction.ordersTitle', 'Inbox orders')}</CardTitle>
            <CardDescription>
              {t('integrations.etsy.toProduction.ordersDescription', '{{count}} inbox orders available for handoff.', {
                count: sortedOrders.length,
                defaultValue_plural: '{{count}} inbox orders available for handoff.',
              })}
            </CardDescription>
          </CardHeader>
          <CardContent className="min-h-0">
            {status === 'loading' && orders.length === 0 ? (
              <div className="flex items-center justify-center py-16 text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('common.loading', 'Loading')}
              </div>
            ) : null}

            {error ? (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
                {error}
              </div>
            ) : null}

            {!error && sortedOrders.length > 0 ? (
              <ScrollArea className="h-[calc(100vh-20rem)] pr-4">
                <div className="space-y-3">
                  {sortedOrders.map((order) => {
                    const missingAreas = getMissingAreas(order);
                    const isSelected = selectedOrder?.id === order.id;

                    const selectOrder = () => setSearchParams((current) => {
                      const next = new URLSearchParams(current);
                      next.set(ETSY_SELECTED_ORDER_QUERY_KEY, order.id);
                      return next;
                    });

                    return (
                      <div
                        key={order.id}
                        role="button"
                        tabIndex={0}
                        onClick={selectOrder}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            selectOrder();
                          }
                        }}
                        className={`w-full rounded-lg border p-4 text-left transition-colors ${isSelected ? 'border-primary bg-muted/25' : 'border-border/60 bg-background hover:bg-muted/10'}`}
                      >
                        <div className="flex items-start gap-3">
                          <EtsyOrderPreviewThumbnail
                            order={order}
                            className="h-16 w-16 shrink-0"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                              <div className="space-y-2">
                                <div className="text-sm font-semibold text-foreground">#{order.etsyReceiptId}</div>
                                <div className="text-xs text-muted-foreground">
                                  {order.shopName} · {order.etsyData.name || t('integrations.etsy.common.unknownBuyer', 'Unknown buyer')} · {formatDateTime(order.importedAt)}
                                </div>
                              </div>
                              <Button
                                type="button"
                                size="sm"
                                variant="default"
                                disabled={!canOpenInProduction(order) || openingOrderId === order.id}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  if (getMissingAreas(order).length > 0 || getEtsyProductionWarnings(order).length > 0) {
                                    setSearchParams((current) => {
                                      const next = new URLSearchParams(current);
                                      next.set(ETSY_SELECTED_ORDER_QUERY_KEY, order.id);
                                      return next;
                                    });
                                    setConfirmOrderId(order.id);
                                    return;
                                  }
                                  void handleOpen(order);
                                }}
                                className="h-8 bg-primary text-primary-foreground shadow-none hover:bg-primary/90"
                              >
                                {openingOrderId === order.id ? (
                                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <ArrowRight className="mr-2 h-3.5 w-3.5" />
                                )}
                                {isReadyForProduction(order)
                                  ? t('integrations.etsy.toProduction.readyForFlow', 'Ready for production flow')
                                  : t('integrations.etsy.toProduction.finishInFlow', 'Finish in production flow')}
                              </Button>
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2">
                              {getEtsyProductionWarnings(order).length > 0 ? (
                                <Badge variant="outline" className="rounded-[6px] border border-amber-200 bg-amber-50 text-amber-800 shadow-none">
                                  {t('integrations.etsy.toProduction.aspectRatioWarningBadge', 'Image ratio warning')}
                                </Badge>
                              ) : null}
                              {missingAreas.length > 0 ? missingAreas.map((area) => (
                                <Badge key={`${order.id}-${area}`} variant="outline" className="rounded-[6px] border border-amber-200 bg-amber-50 text-amber-800 shadow-none">
                                  {getMissingAreaLabel(area, t)}
                                </Badge>
                              )) : (
                                <Badge variant="outline" className="rounded-[6px] border border-emerald-200 bg-emerald-50 text-emerald-800 shadow-none">
                                  {t('integrations.etsy.toProduction.allMatched', 'All key data matched')}
                                </Badge>
                              )}
                              {!canOpenInProduction(order) ? (
                                <Badge variant="outline" className="rounded-[6px] border border-border bg-muted/35 text-muted-foreground shadow-none">
                                  {order.queueStatus}
                                </Badge>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            ) : null}
          </CardContent>
        </Card>

        <Card className="min-h-0">
          <CardHeader>
            <CardTitle>{selectedOrder ? `Receipt #${selectedOrder.etsyReceiptId}` : t('integrations.etsy.toProduction.selectionTitle', 'Order summary')}</CardTitle>
            <CardDescription>
              {selectedOrder
                ? t('integrations.etsy.toProduction.selectionDescription', 'Open the order now or return to earlier Etsy steps to finish matching first.')
                : t('integrations.etsy.toProduction.selectionEmpty', 'Select an Etsy inbox order to review its handoff summary.')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {selectedOrder ? (
              <>
                <div className="rounded-lg border border-border/60 bg-muted/20 p-4">
                  <div className="text-sm font-semibold text-foreground">{selectedOrder.shopName}</div>
                  <div className="mt-1 text-sm text-muted-foreground">
                    {selectedOrder.etsyData.name || t('integrations.etsy.common.unknownBuyer', 'Unknown buyer')}
                  </div>
                  <div className="mt-1 text-sm text-muted-foreground">
                    {selectedOrder.items.length} {t('integrations.etsy.toProduction.itemsLabel', 'items')}
                  </div>
                </div>

                <div className="rounded-lg border border-border/60 p-4">
                  <div className="mb-3 text-sm font-semibold text-foreground">
                    {t('integrations.etsy.toProduction.summaryTitle', 'Matching summary')}
                  </div>
                  <div className="space-y-2 text-sm">
                    {(['papers', 'sizes', 'addresses', 'images'] as MissingArea[]).map((area) => {
                      const missing = selectedMissingAreas.includes(area);
                      return (
                        <div key={area} className="flex items-center justify-between gap-3 rounded-md bg-muted/20 px-3 py-2">
                          <span className="text-foreground">{getMissingAreaLabel(area, t)}</span>
                          <Badge variant="outline" className={missing ? 'rounded-[6px] border border-amber-200 bg-amber-50 text-amber-800 shadow-none' : 'rounded-[6px] border border-emerald-200 bg-emerald-50 text-emerald-800 shadow-none'}>
                            {missing
                              ? t('integrations.etsy.toProduction.missing', 'Missing')
                              : t('integrations.etsy.toProduction.ready', 'Ready')}
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {selectedMissingAreas.length > 0 ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50/80 p-4 text-sm text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/20 dark:text-amber-100">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <div>
                        {t('integrations.etsy.toProduction.warning', 'This order can open in production, but some Etsy data is still incomplete: {{items}}.', {
                          items: selectedMissingAreas.map((area) => getMissingAreaLabel(area, t)).join(', '),
                        })}
                      </div>
                    </div>
                  </div>
                ) : null}

                {selectedProductionWarningSummary ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50/80 p-4 text-sm text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/20 dark:text-amber-100">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <div>{selectedProductionWarningSummary}</div>
                    </div>
                  </div>
                ) : null}

                <div className="flex justify-end">
                  <Button
                    type="button"
                    disabled={!canOpenInProduction(selectedOrder) || openingOrderId === selectedOrder.id}
                    onClick={() => {
                      if (selectedMissingAreas.length > 0 || selectedProductionWarnings.length > 0) {
                        setConfirmOrderId(selectedOrder.id);
                        return;
                      }

                      void handleOpen(selectedOrder);
                    }}
                  >
                    {openingOrderId === selectedOrder.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ArrowRight className="mr-2 h-4 w-4" />}
                    {t('integrations.etsy.toProduction.openButton', 'Open in Production')}
                  </Button>
                </div>

                <ConfirmAlertDialog
                  open={confirmOrderId === selectedOrder.id}
                  onOpenChange={(open) => setConfirmOrderId(open ? selectedOrder.id : null)}
                  title={t('integrations.etsy.toProduction.confirmTitle', 'Open with Etsy handoff warnings?')}
                  description={[
                    selectedMissingAreas.length > 0
                      ? t('integrations.etsy.toProduction.confirmDescription', 'This order will open in production with missing data: {{items}}. You can continue now and finish the missing matching later.', {
                          items: selectedMissingAreas.map((area) => getMissingAreaLabel(area, t)).join(', '),
                        })
                      : null,
                    selectedProductionWarningSummary,
                  ].filter(Boolean).join(' ')}
                  confirmText={t('integrations.etsy.toProduction.confirmButton', 'Open anyway')}
                  cancelText={t('common.cancel', 'Cancel')}
                  onConfirm={() => void handleOpen(selectedOrder)}
                />
              </>
            ) : (
              <div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-10 text-center text-sm text-muted-foreground">
                {t('integrations.etsy.toProduction.selectionEmpty', 'Select an Etsy inbox order to review its handoff summary.')}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EtsyToProductionCanvas;
